import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, Download } from "lucide-react";
import { toast } from "sonner";

const REVIEWED_STATUSES = ["understood", "edited", "confusing", "suggested"];

type Reviewer = { user_id: string; email: string };

type Detail = {
  doc_id: string;
  title: string;
  reviewed_on: string | null;
  rewritten: boolean;
  first_sent: string | null;
  flags: number;
};

type Summary = {
  user_id: string;
  email: string;
  reviewed: number;
  rewritten: number;
  flags: number;
};

function firstOfMonth(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
}

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

function dayOnly(iso: string | null): string {
  return iso ? iso.slice(0, 10) : "";
}

export function ReviewerPayAdmin() {
  const [from, setFrom] = useState(firstOfMonth());
  const [to, setTo] = useState(today());
  const [loading, setLoading] = useState(true);
  const [reviewers, setReviewers] = useState<Reviewer[]>([]);
  const [summary, setSummary] = useState<Summary[]>([]);
  const [details, setDetails] = useState<Record<string, Detail[]>>({});
  const [selected, setSelected] = useState<string>("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const fromIso = new Date(`${from}T00:00:00.000Z`).toISOString();
      const toIso = new Date(`${to}T23:59:59.999Z`).toISOString();

      const { data: roleRows, error: roleErr } = await supabase
        .from("user_roles")
        .select("user_id")
        .eq("role", "reviewer");
      if (roleErr) throw roleErr;

      const ids = Array.from(new Set((roleRows || []).map((r) => r.user_id)));
      if (ids.length === 0) {
        setReviewers([]);
        setSummary([]);
        setDetails({});
        return;
      }

      const [profilesRes, progressRes, proposalsRes, feedbackRes] = await Promise.all([
        supabase.from("profiles").select("id,email").in("id", ids),
        supabase
          .from("reviewer_doc_progress")
          .select("user_id,doc_id,status,updated_at")
          .in("user_id", ids)
          .gte("updated_at", fromIso)
          .lte("updated_at", toIso),
        supabase
          .from("doc_edit_proposals")
          .select("editor_id,doc_id,status,created_at")
          .in("editor_id", ids)
          .neq("status", "draft")
          .gte("created_at", fromIso)
          .lte("created_at", toIso),
        supabase
          .from("doc_feedback")
          .select("user_id,doc_id,created_at")
          .in("user_id", ids)
          .gte("created_at", fromIso)
          .lte("created_at", toIso),
      ]);

      for (const r of [profilesRes, progressRes, proposalsRes, feedbackRes]) {
        if (r.error) throw r.error;
      }

      const emailById = new Map<string, string>();
      (profilesRes.data || []).forEach((p: { id: string; email: string | null }) =>
        emailById.set(p.id, p.email || p.id),
      );

      const docIds = new Set<string>();
      (progressRes.data || []).forEach((r) => docIds.add(r.doc_id));
      (proposalsRes.data || []).forEach((r) => docIds.add(r.doc_id));
      (feedbackRes.data || []).forEach((r) => r.doc_id && docIds.add(r.doc_id));

      const titleById = new Map<string, string>();
      if (docIds.size > 0) {
        const { data: docs } = await supabase
          .from("sop_documents")
          .select("doc_id,title")
          .in("doc_id", Array.from(docIds));
        (docs || []).forEach((d) => titleById.set(d.doc_id, d.title));
      }

      const perUser = new Map<string, Map<string, Detail>>();
      const ensure = (uid: string, docId: string): Detail => {
        if (!perUser.has(uid)) perUser.set(uid, new Map());
        const m = perUser.get(uid)!;
        if (!m.has(docId)) {
          m.set(docId, {
            doc_id: docId,
            title: titleById.get(docId) || "",
            reviewed_on: null,
            rewritten: false,
            first_sent: null,
            flags: 0,
          });
        }
        return m.get(docId)!;
      };

      (progressRes.data || []).forEach((r) => {
        const row = ensure(r.user_id, r.doc_id);
        if (REVIEWED_STATUSES.includes(r.status)) {
          if (!row.reviewed_on || r.updated_at < row.reviewed_on) row.reviewed_on = r.updated_at;
        }
      });
      (proposalsRes.data || []).forEach((r) => {
        const row = ensure(r.editor_id, r.doc_id);
        row.rewritten = true;
        if (!row.first_sent || r.created_at < row.first_sent) row.first_sent = r.created_at;
      });
      (feedbackRes.data || []).forEach((r) => {
        if (!r.doc_id) return;
        const row = ensure(r.user_id, r.doc_id);
        row.flags += 1;
      });

      const nextDetails: Record<string, Detail[]> = {};
      const nextSummary: Summary[] = [];
      const nextReviewers: Reviewer[] = [];

      ids.forEach((uid) => {
        const rows = Array.from(perUser.get(uid)?.values() || []).sort((a, b) =>
          a.doc_id.localeCompare(b.doc_id),
        );
        if (rows.length === 0) return;
        const email = emailById.get(uid) || uid;
        nextReviewers.push({ user_id: uid, email });
        nextDetails[uid] = rows;
        nextSummary.push({
          user_id: uid,
          email,
          reviewed: rows.filter((r) => r.reviewed_on).length,
          rewritten: rows.filter((r) => r.rewritten).length,
          flags: rows.reduce((n, r) => n + r.flags, 0),
        });
      });

      nextReviewers.sort((a, b) => a.email.localeCompare(b.email));
      nextSummary.sort((a, b) => a.email.localeCompare(b.email));

      setReviewers(nextReviewers);
      setSummary(nextSummary);
      setDetails(nextDetails);
      setSelected((prev) => (prev === "all" || (prev && nextDetails[prev]) ? prev : "all"));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not load reviewer activity");
    } finally {
      setLoading(false);
    }
  }, [from, to]);

  useEffect(() => {
    load();
  }, [load]);

  const selectedRows = useMemo(() => {
    const pick = selected === "all" ? reviewers : reviewers.filter((r) => r.user_id === selected);
    const rows = pick.flatMap((r) =>
      (details[r.user_id] || []).map((d) => ({ ...d, email: r.email })),
    );
    return rows.sort((a, b) => a.doc_id.localeCompare(b.doc_id) || a.email.localeCompare(b.email));
  }, [details, reviewers, selected]);

  const exportCsv = () => {
    const header = ["Reviewer", "Doc id", "Title", "Reviewed on", "Rewritten", "First sent on", "Flags"];
    const lines = [header, ...selectedRows.map((r) => [
      r.email,
      r.doc_id,
      r.title,
      dayOnly(r.reviewed_on),
      r.rewritten ? "Yes" : "No",
      dayOnly(r.first_sent),
      String(r.flags),
    ])]
      .map((cols) => cols.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([lines], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `reviewer-pay-${today()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4">
      <Card className="p-4 flex flex-wrap items-end gap-4">
        <div className="space-y-1">
          <Label htmlFor="pay-from" className="text-xs">From</Label>
          <Input id="pay-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-44" />
        </div>
        <div className="space-y-1">
          <Label htmlFor="pay-to" className="text-xs">To</Label>
          <Input id="pay-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-44" />
        </div>
        <Button variant="outline" onClick={load} disabled={loading}>
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Refresh"}
        </Button>
      </Card>

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : summary.length === 0 ? (
        <Card className="p-8 text-center text-muted-foreground">
          No reviewer activity in this range.
        </Card>
      ) : (
        <>
          <Card className="p-0 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Reviewer</TableHead>
                  <TableHead className="text-right">Pages reviewed</TableHead>
                  <TableHead className="text-right">Pages rewritten</TableHead>
                  <TableHead className="text-right">Flags sent</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {summary.map((s) => (
                  <TableRow key={s.user_id}>
                    <TableCell className="text-sm">{s.email}</TableCell>
                    <TableCell className="text-right">{s.reviewed}</TableCell>
                    <TableCell className="text-right">{s.rewritten}</TableCell>
                    <TableCell className="text-right">{s.flags}</TableCell>
                  </TableRow>
                ))}
                {summary.length > 1 && (
                  <TableRow className="font-medium">
                    <TableCell className="text-sm">Totals</TableCell>
                    <TableCell className="text-right">
                      {summary.reduce((n, s) => n + s.reviewed, 0)}
                    </TableCell>
                    <TableCell className="text-right">
                      {summary.reduce((n, s) => n + s.rewritten, 0)}
                    </TableCell>
                    <TableCell className="text-right">
                      {summary.reduce((n, s) => n + s.flags, 0)}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Card>
          <p className="text-xs text-muted-foreground">
            A page counts once, on the first day it was marked Done. Sending a rewrite more than once does not add a page.
          </p>

          <div className="flex flex-wrap items-center gap-3">
            <Select value={selected} onValueChange={setSelected}>
              <SelectTrigger className="w-72">
                <SelectValue placeholder="Choose a reviewer" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All reviewers</SelectItem>
                {reviewers.map((r) => (
                  <SelectItem key={r.user_id} value={r.user_id}>{r.email}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={exportCsv} disabled={selectedRows.length === 0}>
              <Download className="h-4 w-4 mr-2" />
              Download CSV
            </Button>
          </div>

          <Card className="p-0 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Reviewer</TableHead>
                  <TableHead>Doc id</TableHead>
                  <TableHead>Title</TableHead>
                  <TableHead>Reviewed on</TableHead>
                  <TableHead>Rewritten</TableHead>
                  <TableHead>First sent on</TableHead>
                  <TableHead className="text-right">Flags</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {selectedRows.map((r) => (
                  <TableRow key={`${r.email}-${r.doc_id}`}>
                    <TableCell className="text-sm">{r.email}</TableCell>
                    <TableCell className="font-mono text-xs">{r.doc_id}</TableCell>
                    <TableCell className="text-sm">{r.title}</TableCell>
                    <TableCell className="text-sm">{dayOnly(r.reviewed_on)}</TableCell>
                    <TableCell className="text-sm">{r.rewritten ? "Yes" : "No"}</TableCell>
                    <TableCell className="text-sm">{dayOnly(r.first_sent)}</TableCell>
                    <TableCell className="text-right">{r.flags}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </>
      )}
    </div>
  );
}
